import AppConfig from '../appConfig';
import BaseMap from '../enums/BaseMapTypes';
import FacilityPinConfig from '../enums/FacilityPinConfig';
import LibraryGroup from '../objects/LibraryGroup';
import { isStorageAvailable } from './Util';

// TODO: remove the legacy localStorage seeding (and the key itself) once the
// server-side map defaults have been live long enough for every active user to
// have written one.
const LEGACY_STORAGE = window.localStorage;
const LEGACY_STORAGE_KEY = `${AppConfig.constants.environment}-MapLayerPreferences`;

let defaults = {};
let enabled = false;
let libraryGroupDefinitions = [];
let savedLayers = [];
let pendingWrite = null;

function readLegacy() {
    if (!isStorageAvailable(LEGACY_STORAGE)) return {};
    try {
        const stored = JSON.parse(LEGACY_STORAGE.getItem(LEGACY_STORAGE_KEY));
        if (!stored) return {};
        return {
            isSatelliteVisible: !!stored.isSatelliteVisible,
            libraryGroups: stored.groups || {},
        };
    } catch (e) {
        return {};
    }
}

function captureLibraryGroups(mapInstance) {
    const groups = {};
    (mapInstance.libraryDataLayers || []).forEach(group => {
        const layers = {};
        (group.layers || []).forEach(layer => {
            layers[layer.id] = layer.visible;
        });
        groups[group.id] = { visible: group.visible, layers };
    });
    return groups;
}

function restoredPinConfig() {
    return defaults.facilityPinConfig === FacilityPinConfig.BY_TYPE
        ? FacilityPinConfig.BY_TYPE
        : FacilityPinConfig.LEGACY;
}

function flush(bus) {
    clearTimeout(pendingWrite);
    const snapshot = defaults;
    pendingWrite = setTimeout(
        () => bus.emit('MAP_DEFAULTS_UPDATE_REQUEST', { map_defaults: snapshot }),
        500,
    );
}

export function initMapDefaults({ stored, isDefaultProject, libraryGroups }) {
    enabled = !!isDefaultProject;
    libraryGroupDefinitions = libraryGroups || [];
    savedLayers = [];
    defaults = enabled ? { ...readLegacy(), ...(stored || {}) } : {};
}

export function getMapDefaults() {
    return defaults;
}

export function saveMapDefaults(bus, patch) {
    defaults = { ...defaults, ...patch };
    if (enabled && bus) flush(bus);
}

export function saveMapLayers(bus, mapInstance) {
    if (!mapInstance) return;
    saveMapDefaults(bus, {
        isSatelliteVisible: mapInstance.isSatelliteVisible,
        libraryGroups: captureLibraryGroups(mapInstance),
    });
}

export function rememberSavedLayers(mapInstance) {
    savedLayers = ((mapInstance && mapInstance.libraryDataLayers) || [])
        .filter(group => group.id !== BaseMap.STREET)
        .map(group => {
            const definition = libraryGroupDefinitions.find(d => d.id === group.id);
            return (definition && definition.metadata && definition.metadata.title) || group.title;
        });
}

export function getSavedLayers() {
    return savedLayers;
}

export function isDefaultMap() {
    return enabled;
}

export function leaveDefaultMap() {
    enabled = false;
}

export function resetMap(bus) {
    if (!enabled) {
        window.location.reload();
        return;
    }
    clearTimeout(pendingWrite);
    defaults = {};
    if (isStorageAvailable(LEGACY_STORAGE)) LEGACY_STORAGE.removeItem(LEGACY_STORAGE_KEY);
    bus.emit('MAP_DEFAULTS_UPDATE_REQUEST', { map_defaults: defaults }, () =>
        window.location.reload(),
    );
}

export function markLibraryGroupRemoved(bus, groupId, removed) {
    const others = (defaults.removedGroups || []).filter(id => id !== groupId);
    saveMapDefaults(bus, { removedGroups: removed ? others.concat(groupId) : others });
}

export function applyMapDefaults(mapInstance) {
    if (!enabled || !mapInstance) return;

    if ('isSatelliteVisible' in defaults) {
        mapInstance.isSatelliteVisible = !!defaults.isSatelliteVisible;
    }
    if ('facilityPinConfig' in defaults) {
        mapInstance.facilityPinConfig = restoredPinConfig();
    }
    if ('dealPinsVisible' in defaults) {
        mapInstance._dealListPinsVisible = defaults.dealPinsVisible !== false;
    }
    if ('facilitiesVisible' in defaults) {
        mapInstance.facilityPinsVisible = defaults.facilitiesVisible !== false;
    }

    if (!Array.isArray(mapInstance.libraryDataLayers)) return;

    const removedGroups = defaults.removedGroups || [];
    mapInstance.libraryDataLayers = mapInstance.libraryDataLayers.filter(
        group => !removedGroups.includes(group.id),
    );

    const storedGroups = defaults.libraryGroups || {};
    Object.keys(storedGroups).forEach(groupId => {
        const stored = storedGroups[groupId];
        if (!stored || removedGroups.includes(groupId)) return;

        let group = mapInstance.libraryDataLayers.find(g => g.id === groupId);

        if (!group) {
            if (groupId === BaseMap.STREET && stored.visible !== true) return;
            group = LibraryGroup.fromDefinition(
                libraryGroupDefinitions.find(definition => definition.id === groupId),
            );
            if (!group) return;
            mapInstance.libraryDataLayers.push(group);
        }

        if ('visible' in stored) {
            group.visible = stored.visible !== false;
        }
        (group.layers || []).forEach(layer => {
            if (stored.layers && layer.id in stored.layers) {
                layer.visible = stored.layers[layer.id];
            }
        });
    });
}

export function applySummaryLevel(mapInstance, map) {
    if (!enabled || !mapInstance || !map || !('summaryLevelId' in defaults)) return;

    // Automatic is stored as null and has to be applied, not skipped: the project
    // definition can ship a preferred level, which "do nothing" would restore instead.
    // `undefined` is what CLEAR_PREFERRED_SUMMARY_LEVEL_REQUEST writes.
    if (!defaults.summaryLevelId) {
        mapInstance.preferredDataLayerId = undefined;
        return;
    }

    // Only a level the geography dropdown actually lists, which is the same
    // `visible` filter MapData#visibleSummaryLevels applies.
    const summaryLevel = (map.summaryLevels || []).find(
        sl => sl && sl.id === defaults.summaryLevelId && sl.visible,
    );
    if (!summaryLevel) return;

    const dataSource = map.getDataSourceForSummaryLevelId(summaryLevel.id);
    if (dataSource) {
        mapInstance.preferredDataLayerId = dataSource.id;
    }
}
